import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, InfoWindow, LoadScript, Marker, useJsApiLoader } from '@react-google-maps/api';
import { collection, query, getDocs, setDoc, doc } from 'firebase/firestore';
import { auth, db } from '../firebase/firebase';
import { useTranslation } from "react-i18next";
import { signInWithCustomToken } from "firebase/auth"; 
import "./googleMapStyle.css";
import { geocodeStoredAddress, shouldRefreshGeocode } from "./geocodeAddressUtils";

const containerStyle = {
    height: '600px', // Height is now managed by .map-container CSS class - Reverted: height is crucial for GoogleMap component
    maxWidth: '100%',
    position: 'relative',
    overflow: 'hidden'
};

const center = {
    lat: 51.06571144325784,
    lng: 10.376118560697588
};

const mapOptions = {
    // styles: [
    //     {
    //         featureType: 'all',
    //         elementType: 'geometry.stroke',
    //         stylers: [{ visibility: 'off' }]
    //     }
    // ],
    backgroundColor: '#ffffff'
};

const GoogleMapComponent = () => {
    const [markers, setMarkers] = useState([]);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [filter, setFilter] = useState('Alle');
    const [loading, setLoading] = useState(true);
    const { t, i18n } = useTranslation();

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
        language: navigator.language.startsWith('de') ? 'de' : 'en'
    });

    const token = localStorage.getItem('token');
    
    useEffect(() => {
        if (!isLoaded) {
            return;
        }

        if (token) {
            signInWithCustomToken(auth, token)
                .then(() => {
                    fetchAddresses();
                })
                .catch((error) => {
                    // Optionally fetch addresses if authentication fails
                    fetchAddresses();
                });
        } else {
            fetchAddresses();
        }
    }, [isLoaded, token]);
    
    const [allDocIdsDebug, setAllDocIdsDebug] = useState([]);

    const fetchAddresses = async () => {
        try {
            const customerAddressesQuery = query(collection(db, 'customerAddresses'));
            const customerAddressesSnapshot = await getDocs(customerAddressesQuery);

            if (customerAddressesSnapshot.empty) {
                console.warn("No documents found in the customerAddresses collection.");
                setLoading(false);
                return;
            }
            
            const allAddresses = await Promise.all(
                customerAddressesSnapshot.docs.map(async (docSnap) => {
                    const docId = docSnap.id;
                   allDocIdsDebug.push(docId);
                   
                    const addressesCollectionRef = collection(db, `customerAddresses/${docId}/addresses`);
                    const addressesQuerySnapshot = await getDocs(addressesCollectionRef);
                    return addressesQuerySnapshot.docs.map(addressDoc => ({
                        ...addressDoc.data(),
                        docId: addressDoc.id,
                        parentDocId: docId,
                    }));
                })
            );

            // Flatten the array of arrays
            const flattenedAddresses = allAddresses.flat();

            // Geocode only if necessary
            const geocoder = new window.google.maps.Geocoder();

            const geocodedAddresses = [];

            for (const address of flattenedAddresses) {
                if (!shouldRefreshGeocode(address)) {
                    geocodedAddresses.push(address);
                    continue;
                }

                const geocodedAddress = await geocodeStoredAddress(geocoder, address);

                if (geocodedAddress) {
                    const updatedAddress = {
                        ...address,
                        ...geocodedAddress,
                    };

                    await setDoc(doc(db, `customerAddresses/${address.parentDocId}/addresses`, address.docId), updatedAddress);
                    geocodedAddresses.push(updatedAddress);
                    continue;
                }

                geocodedAddresses.push({ ...address, position: address.position || null });
            }

            setMarkers(geocodedAddresses.filter(address => address.position));
            setLoading(false);
        } catch (error) {
            console.error("Error fetching addresses: ", error);
            setLoading(false);
        }
    };

    const convertType = (type, filter) => {
        let convertedType = type;

        if (type === "Physio- & Ergotherapie" && filter === "Physiotherapie") {
            convertedType = "Physiotherapie";
        } else if (type === "Physio- & Ergotherapie" && filter === "Ergotherapie") {
            convertedType = "Ergotherapie";
        } else if (type === "Rehaklinik" && filter === "Klinik"){
            convertedType = "Klinik";
        } else if (type === "Akutklinik" && filter === "Klinik"){
            convertedType = "Klinik";
        }

        return convertedType;
    }

    const filteredMarkers = filter === 'Alle' ? markers : markers.filter(marker => {
        const convertedMarker = convertType(marker.typeOfIns, filter);
        return convertedMarker === filter;
    });
    
    
    //Check if each of the Logo / Clients has at least one adress
    const PrintIfInstitutionHasNoAddress = (allAdresses) => {
        
        if (!allInstitutionNames || !allAdresses || allAdresses.length === 0 || allInstitutionNames.length === 0) return;
        
        const mainInstitutionsWithLogo = allInstitutionNames.map(adress => {return adress.fieldData.name});
        const allAdressesOfTheInstitutions = allAdresses;
        mainInstitutionsWithLogo.forEach((institution) => {
/*
            console.log("Comparing :", institution);
*/
            if (!allAdressesOfTheInstitutions.includes(institution)) {

            }
        });
    }

    if (loading || !isLoaded) {
        return <div>Loading...</div>;
    }

    return (
        <div className="map-container">
                <div className="flex justify-center pt-5 pb-5">
                    <div className="text-center">
                        <h1 className="text-xl font-bold mb-3">{t('filterByInstitutionType')}</h1>
                        <select
                            className="border border-gray-300 rounded-sm px-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 shadow-sm"
                            onChange={(e) => setFilter(e.target.value)}
                            value={filter}
                        >
                            <option value="Alle">{t('Alle')}</option>
                            <option value="Physiotherapie">{t('Physiotherapie')}</option>
                            <option value="Ergotherapie">{t('Ergotherapie')}</option>
                            <option value="Klinik">{t('Klinik')}</option>
                            <option value="Sonstige">{t('Sonstige')}</option>
                        </select>
                    </div>
                </div>
                {isLoaded &&
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={center}
                        zoom={5}
                        options={mapOptions}
                    >
                        {filteredMarkers.map((marker, index) => (
                            <Marker
                                key={index}
                                position={marker.position}
                                title={`${marker.street}, ${marker.city}, ${marker.country}`}
                                onClick={() => {
                                    setSelectedMarker(marker)
                                }}
                            />
                        ))}
    
                        {selectedMarker && (
                            <InfoWindow
                                position={selectedMarker.position}
                                onCloseClick={() => setSelectedMarker(null)}>
                                <div className={"align-top"}>
                                    <h1 className={"text-xl font-bold"}>{selectedMarker.title}</h1>
                                    <h2>{t(selectedMarker.typeOfIns)}</h2>
    
                                    <h2 className={"mt-1 font-bold"}>{t('Adresse')}</h2>
                                    <p>{selectedMarker.street}, {selectedMarker.zip}, {selectedMarker.city}</p>
    
                                    <h2 className={"mt-1 font-bold"}>{t('Kontakt')}</h2>
                                    <a href={selectedMarker.link}>{t('Webseite')}</a>
                                    <p>{t('Tel')}: {selectedMarker.phone}</p>
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>
                }
        </div>

    );
};

export default GoogleMapComponent;
